import type { PayoutMethodSnapshot } from "../../../shared/types";

/** Human labels for a payout method's snapshot keys (order preserved from the saved doc). */
const FIELD_LABELS: Record<string, string> = {
  email: "PayPal email",
  holder: "Account holder",
  bank: "Bank",
  accountNo: "Account no.",
  routing: "Routing (ABA)",
  accountType: "Type",
  iban: "IBAN",
  swift: "SWIFT / BIC",
  wiseEmail: "Wise email",
  tag: "Handle",
};

/** The method's display label (e.g. "PayPal", "Bank transfer"), else a safe default. */
export function methodLabel(method: PayoutMethodSnapshot | null): string {
  if (!method) return "No payout method";
  return method.method?.trim() || "Payout method";
}

/** Ordered copyable [label, value] pairs from a method snapshot (excludes the label key). */
export function methodFields(method: PayoutMethodSnapshot | null): [string, string][] {
  if (!method) return [];
  const isPaypal = methodLabel(method).toLowerCase().includes("paypal");
  const out: [string, string][] = [];
  for (const [key, value] of Object.entries(method)) {
    if (key === "method") continue;
    if (!value) continue;
    let label = FIELD_LABELS[key] ?? key.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());
    if (key === "email" && !isPaypal) label = "Email";
    out.push([label, value]);
  }
  return out;
}

/** "PayPal · sarah@sarahbuilds.co" / "Bank transfer · Dana Lopez", the one line that says where to send. */
export function methodSummary(method: PayoutMethodSnapshot | null): string {
  const label = methodLabel(method);
  const first = methodFields(method)[0];
  return first ? `${label} · ${first[1]}` : label;
}
